import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTable, { createTheme } from "react-data-table-component";
import { Alert, Dropdown, Button } from "react-bootstrap";
import {
  blockUnblockUser,
  deleteTeamMember,
  getTeam,
  passwordReset,
  updateTeamMember,
} from "../../../store/slices/teamSlice";
import { isAdmin, isDJ } from "../../../helpers/utils";
import Badge from "react-bootstrap/Badge";
import SweetAlert from "../../../components/SweetAlert/SweetAlert";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddMember from "../../../components/models/AddMember";
import { customStyles } from "../../../components/Table/tableTheme";
import PageLoader from "../../../components/Loader/PageLoader";
import Swal from "sweetalert2";

// Theme
createTheme(
  "solarizedLight",
  {
    text: {
      primary: "#2c3e50",
      secondary: "#7f8c8d",
    },
    background: {
      default: "#fff",
    },
    context: {
      background: "#f39c12",
      text: "#FFFFFF",
    },
    divider: {
      default: "#bdc3c7",
    },
    action: {
      button: "rgba(0,0,0,0.6)",
      hover: "rgba(0,0,0,0.1)",
      disabled: "rgba(0,0,0,0.3)",
    },
  },
  "light"
);

const Team = () => {
  const dispatch = useDispatch();
  const { members, error, loading } = useSelector((state) => state.team);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    dispatch(getTeam());
  }, [dispatch]);

  const handleSubmit = async (row, action) => {
    try {
      if (action === "block" || action === "unblock") {
        SweetAlert({
          title: `${action} User`,
          text: `Do you want to ${action} user "(${row?.id})" in this channel?`,
          confirmAction: async () => {
            try {
              await dispatch(
                blockUnblockUser({
                  message:
                    action === "block"
                      ? "User banned successfully."
                      : "User unbanned successfully.",
                  values: { ban: action === "block" },
                  id: row?.id,
                })
              ).unwrap();
  
              Swal.fire("Success!", "Action completed successfully.", "success");
            } catch (error) {
              Swal.fire("Error!", error?.message || "Something went wrong", "error");
            }
          },
        });
      }
  
      else if (action === "passwordReset") {
        SweetAlert({
          title: "Password Reset",
          text: `Are you sure you want to reset ${row?.id}'s password?`,
          confirmAction: async () => {
            try {
              await dispatch(
                passwordReset({
                  message: "Password reset successfully.",
                  id: row?.id,
                })
              ).unwrap();
  
              Swal.fire("Success!", "Password reset successfully.", "success");
            } catch (error) {
              Swal.fire("Error!", error?.message || "Reset failed", "error");
            }
          },
        });
      }
  
      else if (action === "delete") {
        if (!row?.id) {
          return Swal.fire("Error!", "Email not found for the current user.", "error");
        }
  
        SweetAlert({
          title: "Delete Team Member",
          text: `Are you sure you want to delete ${row.id}?`,
          showSuccessAlert: false,
          confirmAction: async () => {
            Swal.showLoading();
            try {
              await dispatch(
                deleteTeamMember({
                  message: "Team member deleted successfully.",
                  id: row.id,
                })
              ).unwrap();
  
            } catch (error) {
              console.log(error)
              Swal.fire("Error!", error || "Delete failed", "error");
            }
          },
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const columns = [
    {
      name: "Name",
      cell: (row) => {
        const firstName = row?.attributes?.firstName || "";
        const lastName = row?.attributes?.lastName || "";
        const initials = `${firstName.charAt(0)}${lastName.charAt(
          0
        )}`.toUpperCase();

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className="firstLetterLogo">{initials}</div>
            <strong>
              {firstName} {lastName}
            </strong>
          </div>
        );
      },
    },
    { name: "Email", selector: (row) => row?.id || "N/A" },
    {
      name: "Status",
      cell: (row) => (
        <span style={{ color: row?.attributes?.banned ? "red" : "green" }}>
          {row?.attributes?.banned ? "Banned" : "Active"}
        </span>
      ),
    },
    {
      name: "Role",
      cell: (row) => {
        if (row?.attributes && isAdmin(row?.attributes?.roles?.[0])) {
          return (
            <Badge bg="primary" className="p-2 text-uppercase">
              {row?.attributes?.roles?.[0]}
            </Badge>
          );
        }

        return (
          <Badge bg="secondary" className="p-2 text-uppercase">
            {row?.attributes?.roles?.[0]}
          </Badge>
        );
      },
      style: { textAlign: "left", fontSize: "15px" },
    },
    {
      name: "Actions",
      cell: (row) => {
        const actions = getActionOptions(row);

        if (!actions?.length) return <strong>Owner</strong>;

        return (
          <Dropdown>
            <Dropdown.Toggle
              as={Button}
              variant="light"
              size="sm"
              className="btn btn-outline-secondary "
            >
              Action
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {actions.map((action, index) => (
                <Dropdown.Item key={index} onClick={action.action}>
                  {action.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      style: { textAlign: "center", width: "100px" },
    },
  ];

  const getActionOptions = (row) => {
    const role = row?.attributes?.roles?.[0];

    if (row?.attributes?.alias) return null;

    if (row?.attributes?.banned) {
      return [{ label: "Unblock", action: () => handleSubmit(row, "unblock") },
      { label: "Delete", action: () => handleSubmit(row, "delete") },
      ];
    }

    if (isAdmin(role)) {
      return [
        { label: "Make a DJ", action: () => handleSubmit(row, "dj") },
        {
          label: "Password Reset",
          action: () => handleSubmit(row, "passwordReset"),
        },
        { label: "Block", action: () => handleSubmit(row, "block") },
        { label: "Delete", action: () => handleSubmit(row, "delete") },
      ];
    }

    if (isDJ(role)) {
      return [
        // { label: "Make an Admin", action: () => handleSubmit(row, "admin") },
        {
          label: "Password Reset",
          action: () => handleSubmit(row, "passwordReset"),
        },
        { label: "Block", action: () => handleSubmit(row, "block") },
        { label: "Delete", action: () => handleSubmit(row, "delete") },
      ];
    }

    return [];
  };

  return (
    <div className="section_main_div">
      <div className="d-flex justify-content-between py-4">
        <h2 className="section_heading">Team</h2>
        <button className="btn btn-success" onClick={setShowModal}>
          <AddCircleOutlineIcon /> NEW MEMBER
        </button>
      </div>
      <h5 className="pb-3">Members</h5>
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="custom-shadow position" style={{ position: "relative" }}>
        {loading && (
          <PageLoader />
        )}

        {!loading && (
          <DataTable
            columns={columns}
            data={members ?? []}
            pagination
            highlightOnHover
            theme="solarizedLight"
            customStyles={customStyles}
          />
        )}
      </div>

      <AddMember show={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default Team;